import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    createTableConfig,
    listTableConfigs,
    removeTableConfig,
    tableConfigNotification,
    updateTableConfig,
} from "../../../api/settings/tableConfigs";
import {ReactLocalStorage} from "../../../utils/ReactLocalStorage";
import {buildConfig, isViewChanged, migrateConfig} from "./tableViewConfig";

const byName = (a, b) => a.Name.localeCompare(b.Name);

// ostatnio użyty widok per TableID - lokalnie tylko jego id, ustawienia są zapisane w API
const lastViewStorageKey = (tableId) => `asyncTableLastView_${tableId}`;

/**
 * Zapisane widoki tabeli (settings-api). Domyślny widok to brak zapisanej konfiguracji - ustawienia
 * z propsów tabeli.
 *
 * - lista: moje + udostępnione przez innych (IsOwner: false), pobierana przy wejściu na tabelę,
 * - ostatnio użyty widok (tylko jego id) zapamiętany w localStorage per TableID i stosowany przy wejściu
 *   na tabelę; jeśli został usunięty (albo cofnięto udostępnienie) - widok domyślny,
 * - jeśli aktywny widok zniknie z listy w trakcie pracy, odpinamy go bez zmiany ustawień,
 * - po każdej zmianie element listy jest podmieniany na odpowiedź API (Shared, ModifiedAt liczy backend),
 * - błąd zmiany przeładowuje listę (status HTTP nie jest dostępny, więc 403/404/409 obsługujemy tak samo).
 *
 * @param {Object} params
 * @param {string} [params.tableId] id z TableIDs; bez niego hook nic nie robi
 * @param {Object} params.settings bieżące ustawienia tabeli
 * @param {function(Object)} params.applySettings podmienia ustawienia tabeli ({} - domyślne)
 */
export default function useTableViews({tableId, settings, applySettings}) {
    const enabled = !!tableId;

    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    // pierwsze pobranie listy zakończone (także błędem) - do tego czasu tabela nie wysyła grupowania
    const [settled, setSettled] = useState(false);

    const [activeId, setActiveIdState] = useState(null);

    const setActiveId = useCallback(
        (id) => {
            setActiveIdState(id);
            if (tableId)
                ReactLocalStorage.setPrivate(lastViewStorageKey(tableId), id);
        },
        [tableId]
    );

    // zastosowanie widoku; nieznana wersja zapisu - komunikat zamiast wywracania tabeli
    const applyView = useCallback(
        (item) => {
            const config = migrateConfig(item.Config);
            if (!config) {
                tableConfigNotification("tableViewUnsupported", "error");
                return false;
            }
            applySettings(config.settings);
            return true;
        },
        [applySettings]
    );

    // wejście na tabelę: ostatnio użyty widok; usunięty / nieobsługiwany - domyślny
    const restoreLastView = useCallback(
        (list) => {
            const lastId = ReactLocalStorage.getPrivate(
                lastViewStorageKey(tableId),
                null
            );
            if (!lastId) return;
            const item = list.find((view) => view.ConfigID === lastId);
            if (item && applyView(item)) {
                setActiveIdState(lastId);
                return;
            }
            applySettings({});
            setActiveId(null);
        },
        [tableId, applyView, applySettings, setActiveId]
    );

    const restoreLastViewRef = useRef(restoreLastView);
    restoreLastViewRef.current = restoreLastView;

    const initialized = useRef(false);

    const reload = useCallback(async () => {
        if (!tableId) return;
        setLoading(true);
        try {
            const res = await listTableConfigs(tableId);
            const list = (res?.Items ?? []).slice().sort(byName);
            setItems(list);
            setLoaded(true);
            // ostatni widok stosujemy razem z wczytaniem listy - grupowanie (czeka na `ready`)
            // od razu dostaje jego ustawienia
            if (!initialized.current) {
                initialized.current = true;
                restoreLastViewRef.current(list);
            }
        } catch (e) {
            console.error(e);
            tableConfigNotification("viewLoadFailure", "error");
        } finally {
            setLoading(false);
            setSettled(true);
        }
    }, [tableId]);

    useEffect(() => {
        reload();
    }, [reload]);

    const active = useMemo(
        () => items.find((item) => item.ConfigID === activeId) ?? null,
        [items, activeId]
    );

    // widok usunięty lub odebrany w międzyczasie (po przeładowaniu listy) - wracamy do domyślnego bez zmiany ustawień
    useEffect(() => {
        if (loaded && activeId && !active) setActiveId(null);
    }, [loaded, activeId, active, setActiveId]);

    const replaceItem = useCallback((item) => {
        setItems((prev) =>
            (prev.some((view) => view.ConfigID === item.ConfigID)
                ? prev.map((view) =>
                      view.ConfigID === item.ConfigID ? item : view
                  )
                : [...prev, item]
            ).sort(byName)
        );
    }, []);

    const onMutationError = useCallback(
        (e) => {
            console.error(e);
            tableConfigNotification("viewSaveFailure", "error");
            reload();
        },
        [reload]
    );

    const select = useCallback(
        (id) => {
            if (!id) {
                applySettings({});
                setActiveId(null);
                return;
            }
            const item = items.find((view) => view.ConfigID === id);
            if (item && applyView(item)) setActiveId(id);
        },
        [items, applySettings, applyView, setActiveId]
    );

    // "Zapisz" - tylko własny widok; Config zastępowany w całości
    const save = useCallback(async () => {
        if (!active?.IsOwner) return;
        try {
            const item = await updateTableConfig(tableId, active.ConfigID, {
                Config: buildConfig(settings),
            });
            replaceItem(item);
            tableConfigNotification("viewSaved", "success");
        } catch (e) {
            onMutationError(e);
        }
    }, [active, tableId, settings, replaceItem, onMutationError]);

    // "Zapisz jako…" - także kopia cudzego udostępnionego widoku; nowy widok staje się aktywny
    const saveAs = useCallback(
        async (name, shared) => {
            try {
                const item = await createTableConfig(tableId, {
                    Name: name.trim(),
                    Config: buildConfig(settings),
                    Shared: shared,
                });
                replaceItem(item);
                setActiveId(item.ConfigID);
                tableConfigNotification("viewSaved", "success");
                return true;
            } catch (e) {
                onMutationError(e);
                return false;
            }
        },
        [tableId, settings, replaceItem, setActiveId, onMutationError]
    );

    const update = useCallback(
        async (id, body) => {
            try {
                replaceItem(await updateTableConfig(tableId, id, body));
                return true;
            } catch (e) {
                onMutationError(e);
                return false;
            }
        },
        [tableId, replaceItem, onMutationError]
    );

    const rename = useCallback(
        (id, name) => update(id, {Name: name.trim()}),
        [update]
    );

    const share = useCallback(
        (id, shared) => update(id, {Shared: shared}),
        [update]
    );

    // usunięcie aktywnego widoku wraca do domyślnego
    const remove = useCallback(
        async (id) => {
            try {
                await removeTableConfig(tableId, id);
                setItems((prev) => prev.filter((view) => view.ConfigID !== id));
                if (id === activeId) select(null);
                return true;
            } catch (e) {
                onMutationError(e);
                return false;
            }
        },
        [tableId, activeId, select, onMutationError]
    );

    const changed = useMemo(
        () => isViewChanged(settings, active),
        [settings, active]
    );

    // "Przywróć domyślny" w tabeli - zapomina aktywny widok
    const clearActive = useCallback(() => setActiveId(null), [setActiveId]);

    return {
        enabled,
        // bez tableId nie ma na co czekać
        ready: !enabled || settled,
        items,
        loading,
        active,
        changed,
        reload,
        select,
        save,
        saveAs,
        rename,
        share,
        remove,
        clearActive,
    };
}
